import { buildComposerDraftKey } from "./draftKey";
import type { ComposerDraft, ComposerDraftScope } from "./types";

const DATABASE_NAME = "dolphin-composer-drafts";
const DATABASE_VERSION = 1;
const DRAFT_STORE = "drafts";
const USER_INDEX = "scope.userId";
const UPDATED_AT_INDEX = "updatedAt";
const DEFAULT_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
export const COMPOSER_DRAFT_TARGET_UNAVAILABLE_EVENT =
  "composer-draft-target-unavailable";
export const COMPOSER_DRAFTS_CHANGED_EVENT = "composer-drafts-changed";

export type DraftStorageErrorKind = "unavailable" | "quota";

export class DraftStorageError extends Error {
  readonly kind: DraftStorageErrorKind;
  readonly cause?: unknown;

  constructor(kind: DraftStorageErrorKind, cause?: unknown) {
    super(
      kind === "quota"
        ? "Composer draft storage quota exceeded"
        : "Composer draft storage is unavailable",
    );
    this.name = "DraftStorageError";
    this.kind = kind;
    if (cause !== undefined) this.cause = cause;
  }
}

type DraftOperation<T> = Promise<T | DraftStorageError>;

export interface DraftRepository {
  get(scope: ComposerDraftScope): DraftOperation<ComposerDraft | null>;
  put(draft: ComposerDraft): DraftOperation<void>;
  markTargetUnavailable(scope: ComposerDraftScope): DraftOperation<void>;
  listRecoverable(
    userId: number,
    studyGroupId: number,
  ): DraftOperation<ComposerDraft[]>;
  listForStudyGroup(
    userId: number,
    studyGroupId: number,
  ): DraftOperation<ComposerDraft[]>;
  remove(scope: ComposerDraftScope): DraftOperation<void>;
  removeIfUnchanged(
    expected: ComposerDraft,
  ): DraftOperation<"removed" | "changed">;
  removeByKey(key: string): DraftOperation<void>;
  removeForUser(userId: number): DraftOperation<void>;
  removeExpired(now: number): DraftOperation<void>;
  flush(): Promise<void>;
}

export interface DraftRepositoryOptions {
  indexedDB?: IDBFactory | null;
  retentionMs?: number;
}

const requestResult = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const transactionResult = (transaction: IDBTransaction): Promise<void> =>
  new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });

const errorKind = (error: unknown): DraftStorageErrorKind =>
  typeof error === "object" &&
  error !== null &&
  "name" in error &&
  error.name === "QuotaExceededError"
    ? "quota"
    : "unavailable";

const toStorageError = (error: unknown): DraftStorageError =>
  error instanceof DraftStorageError
    ? error
    : new DraftStorageError(errorKind(error), error);

const getDefaultIndexedDB = (): IDBFactory | null => {
  try {
    return typeof indexedDB === "undefined" ? null : indexedDB;
  } catch {
    return null;
  }
};

const announceTargetUnavailable = (): void => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(COMPOSER_DRAFT_TARGET_UNAVAILABLE_EVENT));
};

const announceRecoverableDraftsChanged = (): void => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(COMPOSER_DRAFTS_CHANGED_EVENT));
};

const cloneScope = (scope: ComposerDraftScope): ComposerDraftScope => {
  switch (scope.kind) {
    case "post":
      return { ...scope };
    case "comment":
      return { ...scope };
    case "reply":
      return { ...scope };
  }
};

const normalizeDraft = (draft: ComposerDraft): ComposerDraft => ({
  version: 1,
  key: buildComposerDraftKey(draft.scope),
  scope: cloneScope(draft.scope),
  content: draft.content,
  hadAttachments: draft.hadAttachments,
  createdAt: draft.createdAt,
  updatedAt: draft.updatedAt,
  ...(draft.targetState === "local-only"
    ? { targetState: "local-only" as const }
    : {}),
});

const isEmptyTextOnlyDraft = (draft: ComposerDraft): boolean =>
  draft.content.trim().length === 0 && !draft.hadAttachments;

const matchesScope = (
  draft: ComposerDraft | undefined,
  scope: ComposerDraftScope,
): draft is ComposerDraft =>
  draft?.version === 1 &&
  draft.key === buildComposerDraftKey(scope) &&
  buildComposerDraftKey(draft.scope) === draft.key;

const matchesRevision = (
  current: ComposerDraft | undefined,
  expected: ComposerDraft,
): current is ComposerDraft =>
  matchesScope(current, expected.scope) &&
  current.version === expected.version &&
  current.key === expected.key &&
  current.content === expected.content &&
  current.hadAttachments === expected.hadAttachments &&
  current.createdAt === expected.createdAt &&
  current.updatedAt === expected.updatedAt &&
  current.targetState === expected.targetState;

export const createDraftRepository = (
  options: DraftRepositoryOptions = {},
): DraftRepository => {
  const factory =
    options.indexedDB === undefined ? getDefaultIndexedDB() : options.indexedDB;
  const retentionMs = options.retentionMs ?? DEFAULT_RETENTION_MS;
  let database: Promise<IDBDatabase> | undefined;
  let queue: Promise<void> = Promise.resolve();

  const openDatabase = async (): Promise<IDBDatabase> => {
    if (!factory) throw new DraftStorageError("unavailable");
    if (!database) {
      database = new Promise((resolve, reject) => {
        const request = factory.open(DATABASE_NAME, DATABASE_VERSION);
        request.onupgradeneeded = () => {
          const store = request.result.createObjectStore(DRAFT_STORE, {
            keyPath: "key",
          });
          store.createIndex(USER_INDEX, USER_INDEX);
          store.createIndex(UPDATED_AT_INDEX, UPDATED_AT_INDEX);
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new DOMException("Database blocked"));
      });
    }

    try {
      return await database;
    } catch (error) {
      database = undefined;
      throw error;
    }
  };

  const withStore = async <T>(
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore) => Promise<T>,
  ): Promise<T> => {
    const db = await openDatabase();
    const transaction = db.transaction(DRAFT_STORE, mode);
    const completed = transactionResult(transaction);
    try {
      const result = await operation(transaction.objectStore(DRAFT_STORE));
      await completed;
      return result;
    } catch (error) {
      await completed.catch(() => undefined);
      throw error;
    }
  };

  const enqueue = <T>(operation: () => Promise<T>): DraftOperation<T> => {
    const result = queue.then(operation).catch(toStorageError);
    queue = result.then(() => undefined);
    return result;
  };

  return {
    get: (scope) =>
      enqueue(async () => {
        const key = buildComposerDraftKey(scope);
        const draft = await withStore("readonly", (store) =>
          requestResult(store.get(key)),
        );
        return matchesScope(draft, scope) ? draft : null;
      }),
    put: (draft) =>
      enqueue(async () => {
        const normalized = normalizeDraft(draft);
        if (isEmptyTextOnlyDraft(normalized)) {
          let removedRecoverableDraft = false;
          await withStore("readwrite", async (store) => {
            const existing = await requestResult<ComposerDraft | undefined>(
              store.get(normalized.key),
            );
            removedRecoverableDraft = existing?.targetState === "local-only";
            await requestResult(store.delete(normalized.key));
          });
          if (removedRecoverableDraft) announceRecoverableDraftsChanged();
          return;
        }
        let savedRecoverableDraft = false;
        await withStore("readwrite", async (store) => {
          const existing = await requestResult<ComposerDraft | undefined>(
            store.get(normalized.key),
          );
          const targetState =
            normalized.targetState === "local-only" ||
            existing?.targetState === "local-only"
              ? "local-only"
              : undefined;
          savedRecoverableDraft = targetState === "local-only";
          await requestResult(
            store.put({
              ...normalized,
              ...(targetState ? { targetState } : {}),
            }),
          );
        });
        if (savedRecoverableDraft) announceRecoverableDraftsChanged();
      }),
    markTargetUnavailable: (scope) =>
      enqueue(async () => {
        const key = buildComposerDraftKey(scope);
        let marked = false;
        let changed = false;
        await withStore("readwrite", async (store) => {
          const draft = await requestResult(store.get(key));
          if (!matchesScope(draft, scope)) return;
          changed = true;
          marked = draft.targetState !== "local-only";
          await requestResult(
            store.put({
              ...normalizeDraft(draft),
              targetState: "local-only",
              updatedAt: Date.now(),
            }),
          );
        });
        if (marked) announceTargetUnavailable();
        if (changed) announceRecoverableDraftsChanged();
      }),
    listRecoverable: (userId, studyGroupId) =>
      enqueue(async () => {
        const drafts = await withStore("readonly", (store) =>
          requestResult(store.index(USER_INDEX).getAll(userId)),
        );
        return drafts.filter(
          (draft) =>
            matchesScope(draft, draft.scope) &&
            draft.scope.studyGroupId === studyGroupId &&
            draft.targetState === "local-only",
        );
      }),
    listForStudyGroup: (userId, studyGroupId) =>
      enqueue(async () => {
        const drafts = await withStore("readonly", (store) =>
          requestResult(store.index(USER_INDEX).getAll(userId)),
        );
        return drafts
          .filter(
            (draft) =>
              matchesScope(draft, draft.scope) &&
              draft.scope.studyGroupId === studyGroupId &&
              (draft.scope.kind === "comment" ||
                draft.scope.kind === "reply") &&
              draft.content.trim().length > 0,
          )
          .sort((left, right) => right.updatedAt - left.updatedAt);
      }),
    remove: (scope) =>
      enqueue(async () => {
        let removedRecoverableDraft = false;
        await withStore("readwrite", async (store) => {
          const key = buildComposerDraftKey(scope);
          const existing = await requestResult<ComposerDraft | undefined>(
            store.get(key),
          );
          removedRecoverableDraft = existing?.targetState === "local-only";
          await requestResult(store.delete(key));
        });
        if (removedRecoverableDraft) announceRecoverableDraftsChanged();
      }),
    removeIfUnchanged: (expected) =>
      enqueue(async () => {
        let result: "removed" | "changed" = "removed";
        let removedRecoverableDraft = false;
        await withStore("readwrite", async (store) => {
          const current = await requestResult<ComposerDraft | undefined>(
            store.get(expected.key),
          );
          if (!current) return;
          if (!matchesRevision(current, expected)) {
            result = "changed";
            return;
          }
          removedRecoverableDraft = current.targetState === "local-only";
          await requestResult(store.delete(expected.key));
        });
        if (removedRecoverableDraft) announceRecoverableDraftsChanged();
        return result;
      }),
    removeByKey: (key) =>
      enqueue(async () => {
        let removedRecoverableDraft = false;
        await withStore("readwrite", async (store) => {
          const existing = await requestResult<ComposerDraft | undefined>(
            store.get(key),
          );
          removedRecoverableDraft = existing?.targetState === "local-only";
          await requestResult(store.delete(key));
        });
        if (removedRecoverableDraft) announceRecoverableDraftsChanged();
      }),
    removeForUser: (userId) =>
      enqueue(async () => {
        let removedRecoverableDraft = false;
        await withStore("readwrite", async (store) => {
          const drafts = await requestResult(
            store.index(USER_INDEX).getAll(userId),
          );
          removedRecoverableDraft = drafts.some(
            (draft) => draft.targetState === "local-only",
          );
          await Promise.all(
            drafts.map((draft) => requestResult(store.delete(draft.key))),
          );
        });
        if (removedRecoverableDraft) announceRecoverableDraftsChanged();
      }),
    removeExpired: (now) =>
      enqueue(async () => {
        let removedRecoverableDraft = false;
        await withStore("readwrite", async (store) => {
          const drafts = await requestResult(store.getAll());
          const expired = drafts.filter(
            (draft) => now - draft.updatedAt >= retentionMs,
          );
          removedRecoverableDraft = expired.some(
            (draft) => draft.targetState === "local-only",
          );
          await Promise.all(
            expired.map((draft) => requestResult(store.delete(draft.key))),
          );
        });
        if (removedRecoverableDraft) announceRecoverableDraftsChanged();
      }),
    flush: () => queue,
  };
};

export const draftRepository = createDraftRepository();
